/**
 * 音声プレビューの「プレビュー条件」(サンプル条件) の状態管理フック — Issue #439。
 *
 * ## 何を解決するか
 *
 * プレビュー条件は
 *
 *   1. 個別フィールドのプレビュー (`AudioPreviewField`)
 *   2. コンテンツ全体・放送地点まるごとの通し試聴 (`BroadcastPointForm`)
 *
 * の **両方** から指定できる必要がある。両者で state 管理を複製すると
 * 「行の設定に追従する / 手入力を尊重する」という肝心のルールが片方だけ
 * 崩れてしまうため、本フックに一元化する。
 *
 * ## 解決規則 (最重要)
 *
 * - **ユーザーが編集済みのキー**: `sample` の値を使う (手動指定を尊重)。
 *   「12 分に変えて試聴」したのに行由来の 4 分で鳴ると意味がない。
 * - **未編集のキー**: `overrides` (編集中の行 / コンテンツ設定から自動導出した値) を
 *   優先する。行の遅れ分数を変えたらサンプル遅延も追従する。
 *
 * 送信値 (`resolve()`) と表示値 (`shown`) は同じ解決規則を通るため、
 * 「画面に出ている値と実際に鳴る音声」が食い違わない。
 *
 * ## 入力枠は「いま設定できる項目」で決まる (Issue #445 / レビュー反映)
 *
 * 接続線・方面は元データが配列なのでプレビュー条件も複数指定できる必要があるが、
 * **行数・方面数はユーザーが自由に増減するものではなく、コンテンツ設定に従う**。
 * 設定に無い接続線をプレビュー条件へ足してもサーバ (`preview_render.py`) は
 * `properties.lines[]` しか辿らないため音声に反映されず、
 * 「入力したのに鳴らない」という分かりにくさが生じるためである。
 *
 * そこで `limits` (= `deriveContentSampleLimits()` / `derivePointSampleLimits()` が
 * 設定内容から導出した入力枠) を受け取り、`resolve()` の時点で
 * **枠に収まる行数・方面数へ切り詰める**。行の追加 / 削除操作は提供しない。
 */
import { useState } from 'react';

import { normalizeSampleContext } from '../previewSample';
import type {
  PreviewSampleContext,
  PreviewSampleLimits,
  PreviewSampleLineContext,
  PreviewSampleLineLimit,
} from '../types';

export interface UsePreviewSampleResult {
  /** 条件パネルの開閉状態 */
  open: boolean;
  /** 条件パネルの開閉を切り替える */
  toggle: () => void;
  /**
   * 条件パネルに表示する値。
   * 未編集の項目は自動導出値 (`overrides`) を表示するため、
   * パネルを開いた時点で「何分の遅延で鳴るのか」が分かる。
   */
  shown: PreviewSampleContext;
  /** サンプル条件の 1 項目を更新し、「ユーザーが編集した」と記録する */
  update: <K extends keyof PreviewSampleContext>(key: K, value: PreviewSampleContext[K]) => void;
  /**
   * 複数条件 (`lines[index]`) の 1 項目を更新する (Issue #445)。
   * 「その行のそのキーだけ手入力した」と記録するため、他の行は overrides に追従し続ける。
   */
  updateLine: <K extends keyof PreviewSampleLineContext>(
    index: number,
    key: K,
    value: PreviewSampleLineContext[K],
  ) => void;
  /** 複数条件の「接続線 index 番目の方面 dirIndex 番目」を更新する (Issue #445) */
  updateDirection: (lineIndex: number, dirIndex: number, value: string) => void;
  /** 実際にサーバへ送るサンプル条件を解決する (表示値 `shown` と同じ規則) */
  resolve: () => PreviewSampleContext;
}

/** `touched` の判定に使うパス表現 (例: `lines[1].directionNames[0]`) */
function linePath(index: number, key: keyof PreviewSampleLineContext): string {
  return `lines[${index}].${key}`;
}

/** 方面 1 つぶんのパス (行と index の両方で一意に決まる) */
function directionPath(lineIndex: number, dirIndex: number): string {
  return `lines[${lineIndex}].directionNames[${dirIndex}]`;
}

export function usePreviewSample({
  initial,
  overrides,
  limits,
}: {
  /**
   * サンプル条件の初期値。
   * 凡例定義 (`buildSampleContext()` / `buildPointSampleContext()`) から作る。
   * 初回レンダー時のみ評価される (以降の変化は `overrides` 経由で反映する)。
   */
  initial: () => PreviewSampleContext;
  /**
   * 自動導出値。編集中の行 / コンテンツの設定値から毎レンダー算出したものを渡す。
   * **未編集のキーにのみ適用** されるため、ユーザーの手入力を壊さない。
   */
  overrides?: Partial<PreviewSampleContext>;
  /**
   * 指定できる入力枠 (接続線の件数 / 各接続線の方面欄数) — Issue #445 (レビュー反映)。
   *
   * `deriveContentSampleLimits()` / `derivePointSampleLimits()` が
   * **コンテンツ設定から** 導出したものを渡す。解決後の `lines[]` はこの枠に
   * 収まるよう切り詰められるため、設定に無い接続線・方面がサーバへ送られない
   * (= 「入力したのに鳴らない」条件が発生しない)。
   *
   * 省略時は切り詰めを行わない (枠が不明なコンテンツでは従来どおりの挙動)。
   */
  limits?: PreviewSampleLimits;
}): UsePreviewSampleResult {
  const [open, setOpen] = useState(false);
  const [sample, setSample] = useState<PreviewSampleContext>(initial);

  /*
   * ユーザーが手で編集したサンプル条件のパス集合。
   * 「未編集のパスだけ overrides を適用する」ために必要
   * (詳細はファイル冒頭の「解決規則」を参照)。
   *
   * 複数条件 (Issue #445) に対応するため、単純なキー名ではなく
   * `lines[1].directionNames[0]` のようなパス文字列を持つ。これにより
   * 「2 行目の方面だけ手入力した → その行だけ overrides で上書きしない」を
   * 正しく判定できる。構造変更 (行の追加 / 削除) は `lines` を touched にする。
   */
  const [touchedKeys, setTouchedKeys] = useState<ReadonlySet<string>>(() => new Set());

  const markTouched = (path: string): void => {
    setTouchedKeys((prev) => new Set(prev).add(path));
  };

  const update = <K extends keyof PreviewSampleContext>(
    key: K,
    value: PreviewSampleContext[K],
  ): void => {
    setSample((prev) => ({ ...prev, [key]: value }));
    markTouched(key);
  };

  /**
   * `lines` を index 安全に書き換える (存在しない行は空オブジェクトで埋める)。
   *
   * 起点は **画面に出ている解決後の値** (`resolveLines()`) にする。
   * state をそのまま起点にすると、自動導出値だけで表示されていた行が
   * 「＋ / −」を押した瞬間に消えてしまう (state は空のまま = 表示と食い違う)。
   * ここで解決後の値を state へ materialize することで、
   * 「見えているものを編集する」という直感どおりの挙動になる。
   */
  const mutateLines = (
    mutate: (lines: PreviewSampleLineContext[]) => PreviewSampleLineContext[],
  ): void => {
    const base = (resolveLines() ?? []).map((line) => ({
      ...line,
      directionNames: line.directionNames ? [...line.directionNames] : undefined,
    }));
    setSample((prev) => ({ ...prev, lines: mutate(base) }));
  };

  const updateLine = <K extends keyof PreviewSampleLineContext>(
    index: number,
    key: K,
    value: PreviewSampleLineContext[K],
  ): void => {
    mutateLines((lines) => {
      while (lines.length <= index) lines.push({});
      lines[index] = { ...lines[index], [key]: value };
      return lines;
    });
    markTouched(linePath(index, key));
    /*
     * 先頭行はフラットキー (`routeName` / `routeId`) と同義なので同時に更新する。
     * part1 (`connecting_line_delay` / `self_train_delay`) や旧サーバは
     * フラットキーしか読まないため、ここを同期しないと「入力したのに鳴らない」
     * という食い違いが起きる。
     */
    if (index === 0 && (key === 'routeName' || key === 'routeId')) {
      setSample((prev) => ({ ...prev, [key]: value as string | undefined }));
      markTouched(key);
    }
  };

  const updateDirection = (lineIndex: number, dirIndex: number, value: string): void => {
    mutateLines((lines) => {
      while (lines.length <= lineIndex) lines.push({});
      const names = [...(lines[lineIndex]?.directionNames ?? [])];
      while (names.length <= dirIndex) names.push('');
      names[dirIndex] = value;
      lines[lineIndex] = { ...lines[lineIndex], directionNames: names };
      return lines;
    });
    markTouched(directionPath(lineIndex, dirIndex));
    // 先頭行の先頭方面はフラットキー `directionName` と同義 (上記と同じ理由)。
    if (lineIndex === 0 && dirIndex === 0) {
      setSample((prev) => ({ ...prev, directionName: value }));
      markTouched('directionName');
    }
  };

  /**
   * 自動導出値のうち「この行に対応するもの」を選ぶ。
   *
   * - 自列車の枠 (`kind: 'self'`) には接続線の自動導出値を適用しない。
   *   適用すると自路線の行に接続線の route_id が混入し、サーバの
   *   `_match_sample_line()` が route_id 一致で先頭の (遅延指定が無い) 行を選んで
   *   「接続線遅延 (分)」の入力が無視される (Issue #664)。
   * - route_id が一致する行を優先し、無ければ同じ並び順の行を使う
   *   (サーバ側 `_match_sample_line()` と同じ規則)。ただし自列車の枠が先頭にあると
   *   接続線の並びが 1 つずれるため、index は自列車の枠を除いた順序で数える。
   */
  const matchOverrideLine = (
    overrideLines: PreviewSampleLineContext[],
    line: PreviewSampleLineContext | undefined,
    index: number,
    limit: PreviewSampleLineLimit | undefined,
    selfCountBefore: number,
  ): PreviewSampleLineContext => {
    if (limit?.kind === 'self') return {};
    const routeId = limit?.routeId ?? line?.routeId;
    if (routeId) {
      const found = overrideLines.find((entry) => entry.routeId === routeId);
      if (found) return found;
    }
    return overrideLines[index - selfCountBefore] ?? {};
  };

  /**
   * 既存の state 行のうち「この枠に対応するもの」を選ぶ。
   * route_id を持つ枠は route_id で、自列車の枠は route_id を持たない行で対応付ける
   * (index だけで対応付けると、接続線の route_id が自路線の行へ混入する)。
   */
  const matchSampleLine = (
    sampleLines: PreviewSampleLineContext[],
    index: number,
    limit: PreviewSampleLineLimit | undefined,
  ): PreviewSampleLineContext => {
    const byIndex = sampleLines[index];
    if (limit?.kind === 'self') {
      return byIndex && !byIndex.routeId ? byIndex : {};
    }
    if (limit?.routeId) {
      const found = sampleLines.find((entry) => entry.routeId === limit.routeId);
      if (found) return found;
      // route_id の無い行 (初期値など) だけ、並び順での対応付けを許す。
      return byIndex && !byIndex.routeId ? byIndex : {};
    }
    return byIndex ?? {};
  };

  /**
   * `lines[]` を解決する。
   *
   * 1. 行数・方面欄数は **`limits` (コンテンツ設定から導出した入力枠)** に従う。
   *    枠が無い (`limits` 未指定) 場合のみ、従来どおり state / overrides の
   *    件数の最大値を採る。
   * 2. 各値は「未編集なら overrides に追従、手入力済みならその値を尊重」する。
   */
  const resolveLines = (): PreviewSampleLineContext[] | undefined => {
    const sampleLines = sample.lines ?? [];
    const overrideLines = overrides?.lines ?? [];
    const limitLines = limits?.lines;
    if (limitLines === undefined && sampleLines.length === 0 && overrideLines.length === 0) {
      return sample.lines;
    }
    /*
     * 入力枠が分かっているならその件数がそのまま行数になる
     * (設定を増減すればプレビュー条件の行も自動で増減する = 常に設定と一致する)。
     */
    const count =
      limitLines !== undefined
        ? limitLines.length
        : Math.max(sampleLines.length, overrideLines.length);

    const resolved: PreviewSampleLineContext[] = [];
    for (let index = 0; index < count; index += 1) {
      const limit = limitLines?.[index];
      const base = matchSampleLine(sampleLines, index, limit);
      const selfCountBefore = (limitLines ?? [])
        .slice(0, index)
        .filter((entry) => entry.kind === 'self').length;
      const derived = matchOverrideLine(overrideLines, base, index, limit, selfCountBefore);
      const entry: PreviewSampleLineContext = { ...base };
      for (const key of ['routeId', 'routeName', 'delaySeconds'] as const) {
        /*
         * 全体の「遅延 (分)」を手入力済みなら、設定から自動導出した行ごとの遅延は適用しない。
         * サーバは `lines[i].delay_seconds` を全体値より優先するため、導出値を残すと
         * 入力した遅延時分が鳴らない (Issue #661)。
         */
        if (key === 'delaySeconds' && touchedKeys.has('delaySeconds')) continue;
        const derivedValue = derived[key];
        if (derivedValue === undefined || touchedKeys.has(linePath(index, key))) continue;
        (entry as Record<string, unknown>)[key] = derivedValue;
      }
      /*
       * 全体の遅延を手入力済みで、この行の遅延が未編集なら行側の値を外し、
       * サーバ側で全体値へフォールバックさせる。行ごとの手入力は従来どおり優先される。
       */
      if (touchedKeys.has('delaySeconds') && !touchedKeys.has(linePath(index, 'delaySeconds'))) {
        delete entry.delaySeconds;
      }
      // 入力枠が route_id を持つなら常にそれを採用する (枠と条件の対応を固定する)。
      if (limit?.routeId) entry.routeId = limit.routeId;

      const baseNames = base.directionNames ?? [];
      const derivedNames = derived.directionNames ?? [];
      /*
       * 方面欄の数も設定 (`limit.maxDirections`) に従う。
       * 設定に無い方面を送るとサーバ側では読み上げられない
       * (part2 は `properties.lines[j].directions[]` の件数ぶんしか鳴らさない) ため、
       * 入力させないほうが「入力したのに鳴らない」混乱を避けられる。
       */
      const nameCount =
        limit !== undefined ? limit.maxDirections : Math.max(baseNames.length, derivedNames.length);
      if (nameCount > 0) {
        const names: string[] = [];
        for (let dirIndex = 0; dirIndex < nameCount; dirIndex += 1) {
          const derivedName = derivedNames[dirIndex];
          const useDerived =
            derivedName !== undefined && !touchedKeys.has(directionPath(index, dirIndex));
          names.push(useDerived ? derivedName : (baseNames[dirIndex] ?? ''));
        }
        entry.directionNames = names;
      } else if (limit !== undefined) {
        // 方面を読み上げないコンテンツでは方面条件そのものを送らない。
        delete entry.directionNames;
      }
      resolved.push(entry);
    }
    return resolved;
  };

  const resolve = (): PreviewSampleContext => {
    const effective: PreviewSampleContext = { ...sample };
    for (const [key, value] of Object.entries(overrides ?? {})) {
      // `lines` は index / route_id 単位の突き合わせが必要なので個別に解決する。
      if (key === 'lines') continue;
      if (value === undefined || touchedKeys.has(key)) continue;
      (effective as Record<string, unknown>)[key] = value;
    }
    const lines = resolveLines();
    if (lines !== undefined) effective.lines = lines;
    /*
     * 先頭が自列車の枠のとき、フラットキー (`routeId` / `routeName` / `directionName`) は
     * 自列車の値を表す。サーバの自列車の描画はフラットキーしか読まないため、
     * 接続線の自動導出値が残っていると自列車の `{route_name}` に接続線の名称が混ざる。
     * 接続線の値は `lines[]` が route_id 単位で持っているので、フラットキーは
     * 自列車の行 (`lines[0]`) の値へ揃える (無ければ空にする)。
     */
    if (lines !== undefined && limits?.lines[0]?.kind === 'self') {
      const selfLine = lines[0];
      effective.routeId = selfLine?.routeId;
      effective.routeName = selfLine?.routeName;
      effective.directionName = selfLine?.directionNames?.[0];
    }
    // フラットキーと lines[] のどちらから見ても同じ条件になるよう正規化して返す。
    return normalizeSampleContext(effective);
  };

  return {
    open,
    toggle: () => setOpen((prev) => !prev),
    shown: resolve(),
    update,
    updateLine,
    updateDirection,
    resolve,
  };
}
